/**
 * Admin copy API. Mirrors engine src/routes/admin.js; every endpoint is requireAdmin.
 */

import { api } from './apiClient';

export type CopyScopeType = 'platform' | 'organization' | 'venue' | 'event';

export interface CopyKey {
  key: string;
  group: string;
  description: string;
  codeDefault: string;
  supportedScopes: CopyScopeType[];
  variables: string[];
  maxLength: number;
  allowEmpty: boolean;
  status: string;
}

export interface CopyResolution extends CopyKey {
  scopeType: CopyScopeType;
  scopeId: string | null;
  /** The override AT THIS SCOPE, or null. Not the same as what renders. */
  override: { value: string; version: number } | null;
  /** What actually renders here. */
  effective: string;
  /** Which level answered: a scope name, or 'code' for the shipped default. */
  effectiveFrom: CopyScopeType | 'code';
}

export interface CopyVersion {
  id: string;
  version: number;
  value: string;
  state: 'current' | 'superseded' | 'reverted';
  isCurrent: boolean;
  createdAt: string;
  createdBy: string | null;
  retiredAt: string | null;
  retiredBy: string | null;
}

const q = (params: Record<string, string | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const out = s.toString();
  return out ? `?${out}` : '';
};

export const listCopyKeys = () => api.get<CopyKey[]>('/admin/copy/keys');

export const resolveCopy = (key: string, scopeType: CopyScopeType, scopeId: string | null) =>
  api.get<CopyResolution>(`/admin/copy/resolve${q({ key, scopeType, scopeId })}`);

export const copyHistory = (key: string, scopeType: CopyScopeType, scopeId: string | null) =>
  api.get<CopyVersion[]>(`/admin/copy/history${q({ key, scopeType, scopeId })}`);

/** `expectedVersion` is the version the editor was looking at; null means "none yet". */
export const saveCopyOverride = (body: {
  key: string; scopeType: CopyScopeType; scopeId: string | null;
  value: string; expectedVersion: number | null;
}) => api.put<{ key: string; version: number; value: string }>('/admin/copy/override', body);

/** Not a delete: the row and its history stay, and the next scope down answers. */
export const revertCopyOverride = (body: {
  key: string; scopeType: CopyScopeType; scopeId: string | null; expectedVersion?: number | null;
}) => api.post<{ key: string; revertedVersion: number }>('/admin/copy/revert', body);
